import { useState, useEffect } from 'react';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { useAuth } from '../contexts/AuthContext';
import { leaveAPI, adminLeaveAPI, workspaceAPI, payrollAPI } from '../lib/api';
import { formatDate } from '../lib/dateFormat';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { StellarDatePicker } from '../components/StellarFormControls';
import { toast } from 'sonner';
import {
  Calendar, Plus, Check, X, Clock, AlertTriangle, ChevronLeft, ChevronRight, Loader2, Pencil
} from 'lucide-react';
import { AutoGrowTextarea } from '../components/AutoGrowTextarea';

const LEAVE_TYPES = [
  { value: 'vacation', label: 'Vacation', color: 'bg-blue-500/15 text-blue-400' },
  { value: 'sick', label: 'Sick', color: 'bg-red-500/15 text-red-400' },
  { value: 'personal', label: 'Personal', color: 'bg-purple-500/15 text-purple-400' },
  { value: 'emergency', label: 'Emergency', color: 'bg-amber-500/15 text-amber-400' },
];

const STATUS_BADGE = {
  pending: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
  approved: 'bg-green-500/10 text-green-400 border-green-500/20',
  rejected: 'bg-red-500/10 text-red-400 border-red-500/20',
  cancelled: 'bg-foreground/[0.05] text-muted-foreground/50 border-border/20',
};

export default function TeamAvailabilityPage() {
  const { currentWorkspace } = useWorkspace();
  const { user } = useAuth();
  const wsId = currentWorkspace?.id;
  const userRole = user?.role || 'team';
  const isAdmin = ['owner', 'admin', 'super_admin'].includes(userRole);

  const [calendarLeaves, setCalendarLeaves] = useState([]);
  const [pending, setPending] = useState([]);
  const [myRequests, setMyRequests] = useState([]);
  const [allLeaves, setAllLeaves] = useState([]);
  const [showRequest, setShowRequest] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  // 2026-06-06 — admin_managed_leave dialog state
  const [showAdminLeave, setShowAdminLeave] = useState(false);
  const [adminLeaveTargetId, setAdminLeaveTargetId] = useState('');
  const [adminLeaveType, setAdminLeaveType] = useState('personal');
  const [adminLeaveStart, setAdminLeaveStart] = useState('');
  const [adminLeaveEnd, setAdminLeaveEnd] = useState('');
  const [adminLeaveNote, setAdminLeaveNote] = useState('');
  const [adminLeaveMembers, setAdminLeaveMembers] = useState([]);
  const [adminLeaveSubmitting, setAdminLeaveSubmitting] = useState(false);
  const [calMonth, setCalMonth] = useState(() => { const n = new Date(); return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}`; });
  // 2026-06-10 — Indian-holiday overlay. Loaded via the Owner-managed
  // public endpoint (no salary fields ever leave the server here).
  const [holidays, setHolidays] = useState([]);
  const [form, setForm] = useState({ leave_type: 'vacation', start_date: '', end_date: '', reason: '' });
  // D64 — filter chip state. Persists per session via sessionStorage.
  const [statusFilter, setStatusFilter] = useState(() => sessionStorage.getItem('availability:statusFilter') || 'all');
  useEffect(() => { sessionStorage.setItem('availability:statusFilter', statusFilter); }, [statusFilter]);
  // 2026-07 — Edit approved leave dialog (Owner/Admin only)
  const [editLeave, setEditLeave] = useState(null);
  const [editStart, setEditStart] = useState('');
  const [editEnd, setEditEnd] = useState('');
  const [editType, setEditType] = useState('personal');
  const [editNote, setEditNote] = useState('');
  const [editSubmitting, setEditSubmitting] = useState(false);
  // 2026-07 — Day detail panel ("who's off on this date")
  const [dayPanel, setDayPanel] = useState(null); // 'YYYY-MM-DD' | null
  const [dayStatusFilter, setDayStatusFilter] = useState('all'); // all | approved | pending

  const fetchAll = async () => {
    if (!wsId) return;
    setLoading(true);
    try {
      const [calRes, pendRes, myRes, allRes] = await Promise.all([
        leaveAPI.calendar(wsId, calMonth),
        isAdmin ? leaveAPI.pending(wsId) : Promise.resolve({ data: [] }),
        leaveAPI.my(wsId),
        leaveAPI.list(wsId),
      ]);
      setCalendarLeaves(calRes.data || []);
      setPending(pendRes.data || []);
      setMyRequests(myRes.data || []);
      setAllLeaves(allRes.data || []);
    } catch {}
    setLoading(false);
  };
  useEffect(() => { fetchAll(); }, [wsId, calMonth]);

  // 2026-06-10 — Load India holidays for the displayed calendar year.
  // Background fetch; non-fatal on failure. Endpoint returns only
  // {date, name, country} — no payroll data.
  useEffect(() => {
    if (!wsId) return;
    const year = Number(calMonth.split('-')[0]);
    payrollAPI.holidaysPublic(wsId, year, 'IN')
      .then((r) => setHolidays(r.data || []))
      .catch(() => setHolidays([]));
  }, [wsId, calMonth]);

  // Calendar date helpers
  const [calYear, calMon] = calMonth.split('-').map(Number);
  const daysInMonth = new Date(calYear, calMon, 0).getDate();
  const firstDay = new Date(calYear, calMon - 1, 1).getDay();
  const calDays = [];
  for (let i = 0; i < firstDay; i++) calDays.push(null);
  for (let d = 1; d <= daysInMonth; d++) calDays.push(d);
  const prevMonth = () => { const d = new Date(calYear, calMon - 2, 1); setCalMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`); };
  const nextMonth = () => { const d = new Date(calYear, calMon, 1); setCalMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`); };

  const getLeavesForDay = (day) => {
    const dateStr = `${calMonth}-${String(day).padStart(2, '0')}`;
    return calendarLeaves.filter(l => {
      const s = l?.start_date;
      const e = l?.end_date || s;
      return s && s <= dateStr && e >= dateStr;
    });
  };

  // Form validation
  const today = new Date().toISOString().slice(0, 10);
  const daysUntilStart = form.start_date ? Math.round((new Date(form.start_date) - new Date(today)) / 86400000) : null;
  const needsAdvanceNotice = ['vacation', 'personal'].includes(form.leave_type);
  const advanceValid = !needsAdvanceNotice || (daysUntilStart !== null && daysUntilStart >= 15);
  const totalDays = form.start_date && form.end_date ? Math.max(1, Math.round((new Date(form.end_date) - new Date(form.start_date)) / 86400000) + 1) : 0;

  const handleSubmit = async () => {
    if (!form.leave_type || !form.start_date || !form.end_date || form.reason.length < 10) {
      toast.error('Please fill all fields. Reason must be at least 10 characters.'); return;
    }
    setSubmitting(true);
    try {
      await leaveAPI.submit(wsId, form);
      toast.success(userRole === 'owner' ? 'Leave auto-approved!' : 'Leave request submitted for approval');
      setShowRequest(false);
      setForm({ leave_type: 'vacation', start_date: '', end_date: '', reason: '' });
      fetchAll();
    } catch (err) { toast.error(typeof err?.response?.data?.detail === 'string' ? err.response.data.detail : 'Failed to submit'); }
    setSubmitting(false);
  };

  const handleVote = async (leaveId, vote) => {
    try {
      const res = await leaveAPI.vote(wsId, leaveId, vote);
      toast.success(res.data.status === 'approved' ? 'Leave approved!' : res.data.status === 'rejected' ? 'Leave rejected' : `Vote recorded (${res.data.approve_count}/${res.data.threshold} approvals)`);
      fetchAll();
    } catch (err) { toast.error(typeof err?.response?.data?.detail === 'string' ? err.response.data.detail : 'Failed'); }
  };

  const handleCancel = async (leaveId) => {
    if (!confirm('Cancel this leave request?')) return;
    try { await leaveAPI.cancel(wsId, leaveId); toast.success('Cancelled'); fetchAll(); } catch { toast.error('Failed'); }
  };

  // 2026-06-06 — admin_managed_leave dialog handlers
  const openAdminLeave = async () => {
    setShowAdminLeave(true);
    setAdminLeaveTargetId('');
    setAdminLeaveType('personal');
    setAdminLeaveStart('');
    setAdminLeaveEnd('');
    setAdminLeaveNote('');
    if (adminLeaveMembers.length === 0 && wsId) {
      try {
        const r = await workspaceAPI.listAssignableMembers(wsId);
        const list = (r.data || [])
          .map((m) => ({ id: m.user_id, name: m.user?.name || m.user?.email || '?', email: m.user?.email || '', role: m.role }))
          .filter((m) => m.id && m.id !== user?.id)
          .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        setAdminLeaveMembers(list);
      } catch {
        setAdminLeaveMembers([]);
      }
    }
  };

  const submitAdminLeave = async () => {
    if (!adminLeaveTargetId) { toast.error('Pick a team member'); return; }
    if (!adminLeaveStart || !adminLeaveEnd) { toast.error('Both start and end dates are required'); return; }
    if (adminLeaveEnd < adminLeaveStart) { toast.error('End date must be on or after start date'); return; }
    setAdminLeaveSubmitting(true);
    try {
      await adminLeaveAPI.create(wsId, {
        target_user_id: adminLeaveTargetId,
        start_date: adminLeaveStart,
        end_date: adminLeaveEnd,
        leave_type: adminLeaveType,
        note: adminLeaveNote,
      });
      toast.success('Approved leave added for team member');
      setShowAdminLeave(false);
      fetchAll();
    } catch (err) {
      toast.error(typeof err?.response?.data?.detail === 'string' ? err.response.data.detail : 'Failed to add leave');
    }
    setAdminLeaveSubmitting(false);
  };

  const monthLabel = new Date(calYear, calMon - 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  // 2026-07 — Edit / Cancel approved leave (Owner/Admin)
  const openEditLeave = (l) => {
    setEditLeave(l);
    setEditStart(l.start_date || '');
    setEditEnd(l.end_date || '');
    setEditType(l.leave_type && l.leave_type !== 'private' ? l.leave_type : 'personal');
    setEditNote('');
  };
  const submitEditLeave = async () => {
    if (!editStart || !editEnd) { toast.error('Both start and end dates are required'); return; }
    if (editEnd < editStart) { toast.error('End date must be on or after start date'); return; }
    setEditSubmitting(true);
    try {
      await adminLeaveAPI.update(wsId, editLeave.id, {
        start_date: editStart, end_date: editEnd, leave_type: editType, note: editNote,
      });
      toast.success('Leave updated — calendar refreshed');
      setEditLeave(null);
      fetchAll();
    } catch (err) {
      toast.error(typeof err?.response?.data?.detail === 'string' ? err.response.data.detail : 'Failed to update leave');
    }
    setEditSubmitting(false);
  };
  const handleAdminCancelLeave = async (l) => {
    if (!confirm(`Cancel ${l.user_name || 'this member'}'s approved leave (${formatDate(l.start_date)} — ${formatDate(l.end_date)})? It will be removed from the calendar but kept in Leave History as cancelled.`)) return;
    try {
      await adminLeaveAPI.cancel(wsId, l.id);
      toast.success('Leave cancelled');
      fetchAll();
    } catch (err) {
      toast.error(typeof err?.response?.data?.detail === 'string' ? err.response.data.detail : 'Failed to cancel leave');
    }
  };

  // 2026-07 — Day panel data: approved + pending leaves covering the date.
  // Uses allLeaves (role-visibility already applied by the backend).
  const getDayPanelLeaves = (dateStr) => {
    if (!dateStr) return [];
    return allLeaves
      .filter(l => ['approved', 'pending'].includes(l.status))
      .filter(l => {
        const s = l.start_date; const e = l.end_date || s;
        return s && s <= dateStr && e >= dateStr;
      })
      .filter(l => dayStatusFilter === 'all' || l.status === dayStatusFilter)
      .sort((a, b) => (a.status === b.status ? (a.user_name || '').localeCompare(b.user_name || '') : (a.status === 'approved' ? -1 : 1)));
  };

  // D64 — Filter chip logic. `all` shows everything; status chips filter by
  // request status. Month chips filter by start_date month vs current.
  const _now = new Date();
  const _thisMonthStr = `${_now.getFullYear()}-${String(_now.getMonth() + 1).padStart(2, '0')}`;
  const _nextMonthDate = new Date(_now.getFullYear(), _now.getMonth() + 1, 1);
  const _nextMonthStr = `${_nextMonthDate.getFullYear()}-${String(_nextMonthDate.getMonth() + 1).padStart(2, '0')}`;
  const _applyFilter = (rows, statusKey = null) => {
    if (statusFilter === 'all') return rows;
    if (statusFilter === 'this_month') return rows.filter(r => (r.start_date || '').startsWith(_thisMonthStr));
    if (statusFilter === 'next_month') return rows.filter(r => (r.start_date || '').startsWith(_nextMonthStr));
    return rows.filter(r => r.status === statusFilter);
  };
  const filteredPending = _applyFilter(pending);
  const filteredMyRequests = _applyFilter(myRequests);
  const filteredHistory = _applyFilter(allLeaves.filter(l => l.status !== 'pending'));

  return (
    <div className="max-w-5xl mx-auto p-4 md:p-8 space-y-6" data-testid="team-availability-page">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-display font-bold">Team Availability</h1>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <Button
              size="sm"
              variant="outline"
              className="border-gold/30 text-gold hover:bg-gold/10"
              onClick={openAdminLeave}
              data-testid="admin-add-leave-btn"
            >
              <Plus className="h-3.5 w-3.5 mr-1.5" /> Add Approved Leave
            </Button>
          )}
          <Button size="sm" className="bg-gold/10 hover:bg-gold/20 text-gold border-0" onClick={() => setShowRequest(true)} data-testid="request-leave-btn">
            <Plus className="h-3.5 w-3.5 mr-1.5" /> Request Leave
          </Button>
        </div>
      </div>

      {/* ── D64 — Status filter chips ── */}
      <div className="flex items-center gap-1.5 flex-wrap" data-testid="availability-filter-chips">
        {[
          { v: 'all', label: 'All' },
          { v: 'pending', label: 'Pending' },
          { v: 'approved', label: 'Approved' },
          { v: 'rejected', label: 'Rejected' },
          { v: 'this_month', label: 'This Month' },
          { v: 'next_month', label: 'Next Month' },
        ].map(c => (
          <button
            key={c.v}
            onClick={() => setStatusFilter(c.v)}
            className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${statusFilter === c.v ? 'bg-gold/10 border-gold/40 text-gold' : 'bg-card border-border/30 text-muted-foreground/70 hover:border-border/50'}`}
            data-testid={`availability-chip-${c.v}`}
          >
            {c.label}
          </button>
        ))}
      </div>

      {/* ── CALENDAR ── */}
      <Card className="bg-card/60 border-border/20">
        <CardHeader className="pb-2 flex-row items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={prevMonth} className="p-1 rounded hover:bg-foreground/[0.05]" data-testid="availability-prev-month"><ChevronLeft className="h-4 w-4" /></button>
            <h2 className="text-[15px] font-semibold">{monthLabel}</h2>
            <button onClick={nextMonth} className="p-1 rounded hover:bg-foreground/[0.05]" data-testid="availability-next-month"><ChevronRight className="h-4 w-4" /></button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-7 gap-px">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
              <div key={d} className="text-[10px] text-muted-foreground/40 text-center py-1 font-medium">{d}</div>
            ))}
            {calDays.map((day, i) => {
              if (!day) return <div key={`e-${i}`} />;
              const dayLeaves = getLeavesForDay(day);
              const dayIso = `${calMonth}-${String(day).padStart(2, '0')}`;
              const isToday = dayIso === today;
              // 2026-06-10 — India holiday overlay (paid, non-working).
              const holiday = holidays.find(h => h.date === dayIso);
              return (
                <div
                  key={day}
                  className={`min-h-[60px] p-1 border border-border/10 rounded cursor-pointer hover:border-gold/30 transition-colors ${isToday ? 'ring-1 ring-gold/30' : ''} ${holiday ? 'bg-purple-500/[0.04]' : ''}`}
                  data-testid={`availability-day-${dayIso}`}
                  onClick={() => { setDayStatusFilter('all'); setDayPanel(dayIso); }}
                  role="button"
                  aria-label={`Show who is off on ${dayIso}`}
                >
                  <div className="flex items-center justify-between">
                    <p className={`text-[11px] ${isToday ? 'text-gold font-bold' : 'text-muted-foreground/50'}`}>{day}</p>
                    {holiday && <span className="text-[8px] text-purple-400" title="India national holiday — paid">🇮🇳</span>}
                  </div>
                  {holiday && (
                    <div
                      className="text-[9px] px-1 py-0.5 rounded mt-0.5 truncate bg-purple-500/15 text-purple-300 border border-purple-500/30"
                      title={`${holiday.name} (India holiday, paid)`}
                      data-testid={`india-holiday-${dayIso}`}
                    >
                      {holiday.name}
                    </div>
                  )}
                  {dayLeaves.slice(0, holiday ? 1 : 2).map(l => (
                    <div
                      key={l.id}
                      className={`text-[9px] px-1 py-0.5 rounded mt-0.5 truncate ${l.created_by_admin ? 'bg-gold/10 text-gold border border-gold/30' : 'bg-foreground/[0.04] text-foreground/70'}`}
                      title={l.created_by_admin ? `${l.display} • admin-approved` : l.display}
                      data-testid={l.created_by_admin ? `admin-leave-tile-${l.id}` : undefined}
                    >
                      {l.created_by_admin ? '★ ' : ''}{l.display}
                    </div>
                  ))}
                  {dayLeaves.length > (holiday ? 1 : 2) && (
                    <p className="text-[8px] text-muted-foreground/30 mt-0.5">+{dayLeaves.length - (holiday ? 1 : 2)} more</p>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* ── PENDING APPROVALS (Owner/Admin only) — D64: always render, empty state included ── */}
      {isAdmin && (
        <Card className="bg-card/60 border-gold/10" data-testid="pending-approvals-section">
          <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Clock className="h-4 w-4 text-gold" /> Pending Approvals ({filteredPending.length})</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {filteredPending.length === 0 ? (
              <p className="text-[12px] text-muted-foreground/40 text-center py-3" data-testid="pending-approvals-empty">
                {statusFilter === 'all' ? 'No pending approvals' : `No pending approvals match the "${statusFilter}" filter`}
              </p>
            ) : filteredPending.map(p => (
              <div key={p.id} className="p-4 rounded-lg border border-border/20 bg-foreground/[0.01]">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <p className="text-[14px] font-semibold text-foreground">{p.requester_name || p.user_name || 'Team member'}</p>
                    <p className="text-[12px] text-muted-foreground/50">{((p.leave_type || '').charAt(0).toUpperCase() + (p.leave_type || '').slice(1)) || '—'} · {p.start_date || '?'} to {p.end_date || p.start_date || '?'} · {p.total_days ?? 1} days</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] text-muted-foreground/40">{p.approve_count ?? 0}/{p.threshold ?? '?'} approvals</span>
                    {!p.my_vote && (<>
                      <Button size="sm" variant="outline" className="h-7 text-[11px] text-green-500 border-green-500/20 hover:bg-green-500/10" onClick={() => handleVote(p.id, 'approve')} data-testid={`approve-leave-${p.id}`}><Check className="h-3 w-3 mr-1" /> Approve</Button>
                      <Button size="sm" variant="outline" className="h-7 text-[11px] text-red-400 border-red-500/20 hover:bg-red-500/10" onClick={() => handleVote(p.id, 'reject')} data-testid={`reject-leave-${p.id}`}><X className="h-3 w-3 mr-1" /> Reject</Button>
                    </>)}
                    {p.my_vote && <Badge className={p.my_vote === 'approve' ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'}>You: {p.my_vote}d</Badge>}
                  </div>
                </div>
                <p className="text-[12px] text-muted-foreground/60">{p.reason || ''}</p>
                <div className="flex gap-2 mt-2">
                  {(p.votes || []).map((v, i) => {
                    const voter = (v?.approver_user_id || '').slice(0, 6) || 'voter';
                    return <Badge key={i} variant="outline" className={`text-[9px] ${v?.vote === 'approve' ? 'border-green-500/20 text-green-400' : 'border-red-500/20 text-red-400'}`}>{voter}: {v?.vote || '—'}</Badge>;
                  })}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* ── MY REQUESTS ── */}
      <Card className="bg-card/60 border-border/20">
        <CardHeader className="pb-2"><CardTitle className="text-sm">My Requests</CardTitle></CardHeader>
        <CardContent>
          {filteredMyRequests.length === 0 ? <p className="text-[13px] text-muted-foreground/40 py-4 text-center">{statusFilter === 'all' ? 'No leave requests yet' : `No requests match the "${statusFilter}" filter`}</p> : (
            <div className="space-y-2">
              {filteredMyRequests.map(r => (
                <div key={r.id} className="flex items-center gap-3 p-3 rounded-lg border border-border/20">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <Badge className={LEAVE_TYPES.find(t => t.value === r.leave_type)?.color || ''}>{r.leave_type?.charAt(0).toUpperCase() + r.leave_type?.slice(1)}</Badge>
                      <Badge className={STATUS_BADGE[r.status] || ''}>{r.status}</Badge>
                    </div>
                    <p className="text-[13px] text-foreground mt-1">{formatDate(r.start_date)} — {formatDate(r.end_date)} ({r.total_days} days)</p>
                    {r.status === 'pending' && <p className="text-[11px] text-muted-foreground/40 mt-0.5">{r.approve_count}/{r.threshold} approvals collected</p>}
                  </div>
                  {(r.status === 'pending' || r.status === 'approved') && (
                    <Button variant="ghost" size="sm" className="text-[11px] text-red-400" onClick={() => handleCancel(r.id)}>Cancel</Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── LEAVE HISTORY ── */}
      <Card className="bg-card/60 border-border/20">
        <CardHeader className="pb-2"><CardTitle className="text-sm">Leave History</CardTitle></CardHeader>
        <CardContent>
          {filteredHistory.length === 0 ? <p className="text-[13px] text-muted-foreground/40 py-4 text-center">No leave history</p> : (
            <div className="space-y-1">
              {filteredHistory.map(l => (
                <div key={l.id} className="flex items-center gap-3 py-2 px-2 rounded hover:bg-foreground/[0.02] group" data-testid={`leave-history-row-${l.id}`}>
                  <span className="text-[13px] font-medium text-foreground w-32 truncate">{l.user_name || 'Team member'}</span>
                  <Badge className={STATUS_BADGE[l.status] || ''} variant="outline">{l.status}</Badge>
                  {l.leave_type !== 'private' && <Badge className={LEAVE_TYPES.find(t => t.value === l.leave_type)?.color || ''} variant="outline">{l.leave_type}</Badge>}
                  <span className="text-[12px] text-muted-foreground/50 flex-1">{formatDate(l.start_date)} — {formatDate(l.end_date)}</span>
                  <span className="text-[11px] text-muted-foreground/30">{l.total_days}d</span>
                  {isAdmin && l.status === 'approved' && (
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        type="button"
                        onClick={() => openEditLeave(l)}
                        className="h-6 w-6 rounded flex items-center justify-center text-muted-foreground/60 hover:text-gold hover:bg-gold/10"
                        title="Edit dates / type"
                        data-testid={`edit-leave-${l.id}`}
                      >
                        <Pencil className="h-3 w-3" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleAdminCancelLeave(l)}
                        className="h-6 w-6 rounded flex items-center justify-center text-muted-foreground/60 hover:text-red-400 hover:bg-red-500/10"
                        title="Cancel this leave"
                        data-testid={`cancel-leave-${l.id}`}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── REQUEST LEAVE DIALOG ── */}
      <Dialog open={showRequest} onOpenChange={setShowRequest}>
        <DialogContent className="sm:max-w-[480px] bg-card border-border/40">
          <DialogHeader><DialogTitle className="font-display text-[17px]">Request Leave</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-[12px] mb-1.5 block">Leave Type *</Label>
              <div className="grid grid-cols-4 gap-2">
                {LEAVE_TYPES.map(t => (
                  <button key={t.value} onClick={() => setForm(f => ({ ...f, leave_type: t.value }))}
                    className={`py-2 rounded-lg text-[12px] font-medium border transition-colors ${form.leave_type === t.value ? 'border-gold/40 bg-gold/5 text-gold' : 'border-border/20 text-muted-foreground/50 hover:border-border/40'}`}>
                    {t.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[12px] mb-1.5 block">Start Date *</Label>
                <StellarDatePicker
                  value={form.start_date}
                  onChange={(d) => setForm(f => ({ ...f, start_date: d }))}
                  placeholder="Select start"
                  testId="leave-start-date"
                  clearable={false}
                />
              </div>
              <div>
                <Label className="text-[12px] mb-1.5 block">End Date *</Label>
                <StellarDatePicker
                  value={form.end_date}
                  onChange={(d) => setForm(f => ({ ...f, end_date: d }))}
                  placeholder="Select end"
                  testId="leave-end-date"
                  minDate={form.start_date || undefined}
                  clearable={false}
                />
              </div>
            </div>
            {form.start_date && (
              <div className="flex items-center gap-3 text-[11px]">
                <span className="text-muted-foreground/50">{daysUntilStart} days from today</span>
                {totalDays > 0 && <span className="text-muted-foreground/50">{totalDays} day{totalDays > 1 ? 's' : ''} total</span>}
                {needsAdvanceNotice && !advanceValid && <span className="text-red-400 flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> 15 days notice required</span>}
              </div>
            )}
            <div>
              <Label className="text-[12px] mb-1.5 block">Reason * (minimum 10 characters)</Label>
              <AutoGrowTextarea value={form.reason} onChange={e => setForm(f => ({ ...f, reason: e.target.value }))} placeholder="Describe your reason for leave..." minRows={6} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowRequest(false)}>Cancel</Button>
            <Button className="bg-gold/10 hover:bg-gold/20 text-gold border-0" onClick={handleSubmit} disabled={submitting} data-testid="submit-leave-btn">
              {submitting ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : null}
              {userRole === 'owner' ? 'Submit (Auto-Approved)' : 'Submit for Approval'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* ── ADMIN APPROVED LEAVE DIALOG ── */}
      <Dialog open={showAdminLeave} onOpenChange={setShowAdminLeave}>
        <DialogContent className="sm:max-w-[480px] bg-card border-border/40" data-testid="admin-leave-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-[17px]">Add Approved Leave</DialogTitle>
            <DialogDescription className="text-[12px] text-muted-foreground/70 sr-only">
              Add an approved leave directly on behalf of a team member.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-[12px] text-muted-foreground/70">
              Add an approved leave directly on behalf of a team member. They do not need to take action — it appears
              on their calendar as an approved leave and does not count as a missed shift.
            </p>
            <div>
              <Label className="text-[12px] mb-1.5 block">Team member *</Label>
              <select
                value={adminLeaveTargetId}
                onChange={(e) => setAdminLeaveTargetId(e.target.value)}
                data-testid="admin-leave-member-select"
                className="w-full h-10 rounded-md bg-background border border-border/40 px-3 text-[13px]"
              >
                <option value="">Select a team member…</option>
                {adminLeaveMembers.map((m) => (
                  <option key={m.id} value={m.id}>{m.name} — {m.role}</option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-[12px] mb-1.5 block">Leave Type *</Label>
              <div className="grid grid-cols-4 gap-2">
                {LEAVE_TYPES.map(t => (
                  <button
                    key={t.value}
                    type="button"
                    onClick={() => setAdminLeaveType(t.value)}
                    className={`py-2 rounded-lg text-[12px] font-medium border transition-colors ${adminLeaveType === t.value ? 'border-gold/40 bg-gold/5 text-gold' : 'border-border/20 text-muted-foreground/60 hover:border-border/40'}`}
                    data-testid={`admin-leave-type-${t.value}`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[12px] mb-1.5 block">Start Date *</Label>
                <StellarDatePicker
                  value={adminLeaveStart}
                  onChange={(d) => setAdminLeaveStart(d)}
                  placeholder="Select start"
                  testId="admin-leave-start"
                  clearable={false}
                />
              </div>
              <div>
                <Label className="text-[12px] mb-1.5 block">End Date *</Label>
                <StellarDatePicker
                  value={adminLeaveEnd}
                  onChange={(d) => setAdminLeaveEnd(d)}
                  placeholder="Select end"
                  testId="admin-leave-end"
                  minDate={adminLeaveStart || undefined}
                  clearable={false}
                />
              </div>
            </div>
            <div>
              <Label className="text-[12px] mb-1.5 block">Note (optional)</Label>
              <AutoGrowTextarea
                value={adminLeaveNote}
                onChange={(e) => setAdminLeaveNote(e.target.value)}
                placeholder="Short note visible to the team member…"
                minRows={6}
                data-testid="admin-leave-note"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowAdminLeave(false)}>Cancel</Button>
            <Button
              className="bg-gold/10 hover:bg-gold/20 text-gold border-0"
              onClick={submitAdminLeave}
              disabled={adminLeaveSubmitting}
              data-testid="admin-leave-submit"
            >
              {adminLeaveSubmitting ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : null}
              Add Approved Leave
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── 2026-07 — EDIT APPROVED LEAVE DIALOG (Owner/Admin) ── */}
      <Dialog open={!!editLeave} onOpenChange={(v) => !v && setEditLeave(null)}>
        <DialogContent className="sm:max-w-[480px] bg-card border-border/40" data-testid="edit-leave-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-[17px]">Edit Approved Leave</DialogTitle>
            <DialogDescription className="text-[12px]">
              {editLeave?.user_name || 'Team member'} · changes update the calendar immediately.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-[12px] mb-1.5 block">Leave type</Label>
              <div className="grid grid-cols-4 gap-2">
                {LEAVE_TYPES.map(t => (
                  <button
                    key={t.value}
                    type="button"
                    onClick={() => setEditType(t.value)}
                    className={`text-[11px] px-2 py-1.5 rounded-md border transition-colors ${editType === t.value ? 'border-gold/50 bg-gold/10 text-gold' : 'border-border/30 text-muted-foreground/70 hover:border-border/50'}`}
                    data-testid={`edit-leave-type-${t.value}`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[12px] mb-1.5 block">Start date</Label>
                <StellarDatePicker value={editStart} onChange={(d) => setEditStart(d)} placeholder="Select start" testId="edit-leave-start" clearable={false} />
              </div>
              <div>
                <Label className="text-[12px] mb-1.5 block">End date</Label>
                <StellarDatePicker value={editEnd} onChange={(d) => setEditEnd(d)} placeholder="Select end" testId="edit-leave-end" minDate={editStart || undefined} clearable={false} />
              </div>
            </div>
            <div>
              <Label className="text-[12px] mb-1.5 block">Update note (optional — replaces current note)</Label>
              <AutoGrowTextarea
                value={editNote}
                onChange={(e) => setEditNote(e.target.value)}
                placeholder={editLeave?.reason ? `Current: ${editLeave.reason}` : 'Add a note…'}
                minRows={3}
                data-testid="edit-leave-note"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditLeave(null)}>Cancel</Button>
            <Button
              className="bg-gold/10 hover:bg-gold/20 text-gold border-0"
              onClick={submitEditLeave}
              disabled={editSubmitting}
              data-testid="edit-leave-submit"
            >
              {editSubmitting ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : null}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── 2026-07 — DAY DETAIL PANEL: who's off on the clicked date ── */}
      <Dialog open={!!dayPanel} onOpenChange={(v) => !v && setDayPanel(null)}>
        <DialogContent className="sm:max-w-[440px] bg-card border-border/40" data-testid="day-leaves-panel">
          <DialogHeader>
            <DialogTitle className="font-display text-[16px]">Who's off — {dayPanel ? formatDate(dayPanel) : ''}</DialogTitle>
            <DialogDescription className="text-[12px]">All leave covering this date.</DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-1.5">
            {[{ v: 'all', label: 'All' }, { v: 'approved', label: 'Approved' }, { v: 'pending', label: 'Pending' }].map(c => (
              <button
                key={c.v}
                type="button"
                onClick={() => setDayStatusFilter(c.v)}
                className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${dayStatusFilter === c.v ? 'bg-gold/10 border-gold/40 text-gold' : 'bg-card border-border/30 text-muted-foreground/70 hover:border-border/50'}`}
                data-testid={`day-panel-chip-${c.v}`}
              >
                {c.label}
              </button>
            ))}
          </div>
          <div className="space-y-1.5 max-h-[50vh] overflow-y-auto" data-testid="day-leaves-list">
            {getDayPanelLeaves(dayPanel).length === 0 ? (
              <p className="text-[13px] text-muted-foreground/40 text-center py-6" data-testid="day-leaves-empty">
                No one is on leave this day{dayStatusFilter !== 'all' ? ` (${dayStatusFilter})` : ''}.
              </p>
            ) : getDayPanelLeaves(dayPanel).map(l => (
              <div key={l.id} className="flex items-center gap-2.5 p-2.5 rounded-lg border border-border/20 bg-foreground/[0.02] group" data-testid={`day-leave-row-${l.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[13px] font-medium text-foreground">{l.user_name || 'Team member'}</span>
                    {l.leave_type !== 'private' && (
                      <Badge className={LEAVE_TYPES.find(t => t.value === l.leave_type)?.color || ''} variant="outline">
                        {(l.leave_type || '').charAt(0).toUpperCase() + (l.leave_type || '').slice(1)}
                      </Badge>
                    )}
                    <Badge className={STATUS_BADGE[l.status] || ''} variant="outline">{l.status}</Badge>
                  </div>
                  <p className="text-[11.5px] text-muted-foreground/55 mt-0.5">
                    {l.start_date === l.end_date
                      ? formatDate(l.start_date)
                      : `${formatDate(l.start_date)} — ${formatDate(l.end_date)} (${l.total_days || 1} days)`}
                  </p>
                </div>
                {isAdmin && l.status === 'approved' && (
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      type="button"
                      onClick={() => { setDayPanel(null); openEditLeave(l); }}
                      className="h-6 w-6 rounded flex items-center justify-center text-muted-foreground/60 hover:text-gold hover:bg-gold/10"
                      title="Edit dates / type"
                      data-testid={`day-panel-edit-${l.id}`}
                    >
                      <Pencil className="h-3 w-3" />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleAdminCancelLeave(l)}
                      className="h-6 w-6 rounded flex items-center justify-center text-muted-foreground/60 hover:text-red-400 hover:bg-red-500/10"
                      title="Cancel this leave"
                      data-testid={`day-panel-cancel-${l.id}`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
